/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Referral Service - API client for referral program endpoints
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import api from './api'
import { createLogger } from '../utils/logger'

const logger = createLogger('referralService')

/**
 * Validate a referral code (public)
 * @param {string} code - The referral code to validate
 * @returns {Promise<Object>} Validation result { valid, referrerPublicName, error }
 */
export const validateReferralCode = async (code) => {
  try {
    const response = await api.get(`/referrals/validate/${encodeURIComponent(code)}`)
    return response.data
  } catch (error) {
    logger.error('Error validating referral code:', error)
    return {
      success: false,
      data: {
        valid: false,
        error: error.response?.data?.error?.message || 'Failed to validate referral code'
      }
    }
  }
}

/**
 * Claim a referral (public, before registration)
 * @param {string} referralCode - The referral code
 * @param {string} email - Email of the person being referred
 * @returns {Promise<Object>} Claim result { referralId, skipWaitlist }
 */
export const claimReferral = async (referralCode, email) => {
  const response = await api.post('/referrals/claim', { referralCode, email })
  return response.data
}

/**
 * Get the current user's referral code and basic stats
 * @returns {Promise<Object>} User's referral profile
 */
export const getMyReferralCode = async () => {
  const response = await api.get('/referrals/my-code')
  return response.data
}

/**
 * Get detailed referral statistics for the current user
 * @returns {Promise<Object>} Detailed referral stats
 */
export const getReferralStats = async () => {
  const response = await api.get('/referrals/stats')
  return response.data
}

/**
 * Get paginated referral history for the current user
 * @param {Object} options - Pagination options { page, limit }
 * @returns {Promise<Object>} Paginated referral history
 */
export const getReferralHistory = async ({ page = 1, limit = 20 } = {}) => {
  const response = await api.get('/referrals/history', {
    params: { page, limit }
  })
  return response.data
}

// ============================================================================
// EMAIL INVITE ENDPOINTS
// ============================================================================

/**
 * Send an email invitation to a friend
 * @param {string} email - Email address to invite
 * @returns {Promise<Object>} Invitation result { id, email, expiresAt, status }
 */
export const sendEmailInvite = async (email) => {
  const response = await api.post('/referrals/invite', { email })
  return response.data
}

/**
 * Resend an email invitation
 * @param {string} referralId - The referral ID to resend
 * @returns {Promise<Object>} Updated invitation { id, email, expiresAt, status }
 */
export const resendInvite = async (referralId) => {
  const response = await api.post(`/referrals/invite/${referralId}/resend`)
  return response.data
}

/**
 * Validate an email invite token (public)
 * @param {string} token - The invite token from the email link
 * @returns {Promise<Object>} Validation result { valid, email, referrerName, referralCode, expiresAt, error }
 */
export const validateInviteToken = async (token) => {
  try {
    const response = await api.get(`/referrals/invite/${encodeURIComponent(token)}/validate`)
    return response.data
  } catch (error) {
    logger.error('Error validating invite token:', error)
    return {
      success: false,
      data: {
        valid: false,
        error: error.response?.data?.error?.message || 'Invalid or expired invitation'
      }
    }
  }
}

// ============================================================================
// ADMIN ENDPOINTS
// ============================================================================

/**
 * Get all referral program settings (admin)
 * @returns {Promise<Object>} All settings
 */
export const getAdminSettings = async () => {
  const response = await api.get('/referrals/admin/settings')
  return response.data
}

/**
 * Update a referral setting (admin)
 * @param {string} key - Setting key
 * @param {*} value - New value
 * @returns {Promise<Object>} Updated setting
 */
export const updateAdminSetting = async (key, value) => {
  const response = await api.put('/referrals/admin/settings', { key, value })
  return response.data
}

/**
 * Grant invites to a user (admin)
 * @param {string} userId - Target user ID
 * @param {number} amount - Number of invites to grant
 * @param {string} reason - Optional reason
 * @returns {Promise<Object>} Grant result
 */
export const grantInvites = async (userId, amount, reason = null) => {
  const response = await api.post('/referrals/admin/grant', { userId, amount, reason })
  return response.data
}

/**
 * Get referral program analytics (admin)
 * @returns {Promise<Object>} Program-wide analytics
 */
export const getAdminAnalytics = async () => {
  const response = await api.get('/referrals/admin/analytics')
  return response.data
}

/**
 * List users with their referral stats (admin)
 * @param {Object} options - Pagination and filter options
 * @returns {Promise<Object>} Paginated user list
 */
export const listUserReferrals = async ({ page = 1, limit = 50, search = '' } = {}) => {
  const response = await api.get('/referrals/admin/users', {
    params: { page, limit, search }
  })
  return response.data
}

/**
 * Manually trigger replenishment job (admin)
 * @returns {Promise<Object>} Replenishment result
 */
export const triggerReplenishment = async () => {
  const response = await api.post('/referrals/admin/replenish')
  return response.data
}

export default {
  validateReferralCode,
  claimReferral,
  getMyReferralCode,
  getReferralStats,
  getReferralHistory,
  // Email invites
  sendEmailInvite,
  resendInvite,
  validateInviteToken,
  // Admin
  getAdminSettings,
  updateAdminSetting,
  grantInvites,
  getAdminAnalytics,
  listUserReferrals,
  triggerReplenishment
}
